import { useEffect, useState } from 'react';

import { subscribeHlRealtime } from '../lib/hlRealtime';
import { useMe } from './useApiQuery';
import type { HlPositionItem } from '../lib/types';

/**
 * Live HL position list pushed from the public ``webData2`` WebSocket.
 *
 * Returns ``null`` until the first snapshot arrives (use that as a
 * "not ready, fall back to /v1/balance/hl polling" signal). After the
 * first message the value updates on every tick — for HL perp pages
 * that's roughly every few seconds when state changes, with mark
 * price floating in between via positionValue updates.
 *
 * Disabled (``enabled=false``) skips the subscription entirely so
 * PM-only sessions don't open a useless WS to HL.
 */
export function useHlRealtimePositions(opts: { enabled: boolean }): {
  positions: HlPositionItem[] | null;
} {
  const me = useMe();
  const wallet = me.data?.wallet_address ?? null;
  const [positions, setPositions] = useState<HlPositionItem[] | null>(null);

  useEffect(() => {
    if (!opts.enabled || !wallet) {
      // Reset stale state when the gate closes — otherwise a user who
      // settles their last HL trade keeps seeing the last snapshot
      // until they navigate away.
      setPositions(null);
      return;
    }
    const unsub = subscribeHlRealtime(wallet, (snap) => {
      setPositions(snap.positions);
    });
    return unsub;
  }, [opts.enabled, wallet]);

  return { positions };
}
